import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CreditCard, Check, ArrowRight, DollarSign, Clock, Shield, Percent, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FinancingPage() {
  const [financingInfo, setFinancingInfo] = useState(null);

  useEffect(() => {
    document.title = "HVAC Financing Options | 0% APR Available | Virgil's Heating & Air";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Flexible HVAC financing options in Greater Sacramento. 0% APR available, low monthly payments, quick approval. Finance your AC, furnace, or mini split installation today.");
    }
  }, []);

  useEffect(() => {
    const fetchFinancing = async () => {
      try {
        const response = await axios.get(`${API}/financing`);
        setFinancingInfo(response.data);
      } catch (e) {
        console.error("Error fetching financing:", e);
      }
    };
    fetchFinancing();
  }, []);

  return (
    <div className="pt-36 min-h-screen bg-[#050505]" data-testid="financing-page">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden bg-[#0a0a0a]" data-testid="financing-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url(https://customer-assets.emergentagent.com/job_94a568bf-da54-41b2-bd7f-4fba373edc52/artifacts/4hycop0c_3C440644-29FA-4B0E-B4AF-A6914647D949.jpeg)",
            filter: "brightness(1.5) contrast(1.04) saturate(1.1)"
          }}
          data-testid="financing-hero-bg"
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/90 via-[#050505]/60 to-[#050505]/25"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent"></div>
        <div className="container-custom relative z-10">
          <p className="font-mono text-[#5b93ff] text-sm uppercase tracking-wider mb-2 font-semibold">Financing</p>
          <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl gradient-text mb-4">
            FLEXIBLE FINANCING OPTIONS
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl">
            Don't let budget concerns stop you from getting the comfort you deserve. 
            We offer flexible financing options to fit your needs.
          </p>
        </div>
      </section>

      {/* Credit Card Visual */}
      <section className="section-padding" data-testid="financing-card-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="financing-card p-8 md:p-12 relative" data-testid="credit-card-visual">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#0066CC] rounded-full blur-[120px] opacity-10"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#DC2626] rounded-full blur-[100px] opacity-10"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-4 bg-gradient-to-r from-[#0066CC] to-[#1E40AF] rounded-2xl shadow-lg">
                    <CreditCard className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-2xl text-white">Virgil's Financing</h2>
                    <p className="text-gray-400">Powered by trusted lending partners</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center p-6 bg-gradient-to-br from-[#0a0a12] to-[#0a0a0a] rounded-2xl">
                    <Percent className="w-10 h-10 text-[#5b93ff] mx-auto mb-3" />
                    <p className="font-heading font-black text-4xl text-white mb-1">0%</p>
                    <p className="text-gray-400 text-sm font-medium">APR Available*</p>
                  </div>
                  <div className="text-center p-6 bg-gradient-to-br from-[#0a0a12] to-[#0a0a0a] rounded-2xl">
                    <Clock className="w-10 h-10 text-[#5b93ff] mx-auto mb-3" />
                    <p className="font-heading font-black text-4xl text-white mb-1">72</p>
                    <p className="text-gray-400 text-sm font-medium">Months Max Term</p>
                  </div>
                  <div className="text-center p-6 bg-gradient-to-br from-[#0a0a12] to-[#0a0a0a] rounded-2xl">
                    <DollarSign className="w-10 h-10 text-[#5b93ff] mx-auto mb-3" />
                    <p className="font-heading font-black text-4xl text-white mb-1">$0</p>
                    <p className="text-gray-400 text-sm font-medium">Down Payment</p>
                  </div>
                </div>

                <p className="text-gray-500 text-xs text-center">*Subject to credit approval. Terms and conditions apply.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Financing Options */}
      {financingInfo && (
        <section className="section-padding bg-white/5" data-testid="financing-options">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-responsive-section gradient-text">
                FINANCING PROGRAMS
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {financingInfo.options.map((option, index) => (
                <div
                  key={index}
                  className={`bg-[#101014] border-2 rounded-2xl p-8 transition-all hover:shadow-xl ${
                    index === 0 ? 'border-[#0066CC] shadow-lg' : 'border-white/10 hover:border-[#0066CC]'
                  }`}
                  data-testid={`financing-option-${index}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono text-xs text-[#5b93ff] uppercase font-bold bg-[#1E6BFF]/10 px-3 py-1 rounded-full">{option.terms}</span>
                    {index === 0 && (
                      <span className="px-3 py-1 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] text-white text-xs font-bold rounded-full">POPULAR</span>
                    )}
                  </div>
                  <h3 className="font-heading font-bold text-xl text-white mb-3">{option.name}</h3>
                  <p className="text-gray-400">{option.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      {financingInfo && (
        <section className="section-padding" data-testid="financing-benefits">
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="font-mono text-[#5b93ff] text-sm uppercase tracking-wider mb-2 font-semibold">Benefits</p>
                <h2 className="font-heading font-bold text-responsive-section gradient-text mb-6">
                  WHY FINANCE WITH US?
                </h2>
                <p className="text-gray-300 text-lg mb-8">
                  {financingInfo.description}
                </p>
                <ul className="space-y-4">
                  {financingInfo.benefits.map((benefit, index) => (
                    <li 
                      key={index} 
                      className="flex items-center gap-4 bg-white/5 p-4 rounded-xl"
                      data-testid={`benefit-${index}`}
                    >
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Check className="w-6 h-6 text-green-600" />
                      </div>
                      <span className="text-white font-medium text-lg">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-[#0a0a12] to-[#0a0a0a] rounded-3xl p-8 shadow-xl">
                <h3 className="font-heading font-bold text-2xl text-white mb-6">
                  Ready to Get Started?
                </h3>
                <p className="text-gray-300 mb-6 text-lg">
                  Apply for financing today and get a decision in minutes. 
                  Our team will help you find the best option for your budget.
                </p>
                
                {/* Hearth Financing */}
                <a 
                  href="https://app.gethearth.com/partners/air-repair-heating-air" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block mb-6"
                  data-testid="hearth-financing-link"
                >
                  <div className="bg-black rounded-2xl p-2 border border-white/10 hover:border-[#1E6BFF] hover:shadow-[0_0_28px_rgba(30,107,255,0.4)] transition-all">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_94a568bf-da54-41b2-bd7f-4fba373edc52/artifacts/ovkv8r2m_A6219B39-E86A-4638-B753-B4C640D9AFEB.png"
                      alt="Virgil's Financing by Hearth - Apply Now"
                      className="w-full h-auto rounded-xl img-sharp"
                    />
                  </div>
                </a>
                
                <div className="space-y-4">
                  <a 
                    href="https://app.gethearth.com/partners/air-repair-heating-air" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button className="btn-primary w-full text-lg py-6" data-testid="apply-financing-btn">
                      Apply for Financing
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </a>
                  <Link to="/booking" className="block">
                    <Button variant="outline" className="btn-outline w-full text-lg py-6" data-testid="schedule-consultation-btn">
                      Schedule a Consultation
                    </Button>
                  </Link>
                  <a href="tel:7073843409" className="block">
                    <Button className="btn-urgent w-full text-lg py-6" data-testid="call-financing-btn">
                      <Phone className="mr-2 w-5 h-5" />
                      Call 707-384-3409
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="section-padding bg-gradient-to-br from-[#0a0a12] to-[#0a0a0a]" data-testid="how-it-works">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-responsive-section gradient-text">
              HOW IT WORKS
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { num: "01", title: "Apply", desc: "Quick online application takes just minutes" },
              { num: "02", title: "Get Approved", desc: "Instant decision in most cases" },
              { num: "03", title: "Schedule", desc: "Book your service appointment" },
              { num: "04", title: "Enjoy", desc: "Make comfortable monthly payments" }
            ].map((step, index) => (
              <div key={index} className="text-center" data-testid={`how-step-${index}`}>
                <div className="font-heading font-black text-6xl text-[#5b93ff]/20 mb-4">{step.num}</div>
                <h3 className="font-heading font-bold text-xl text-white mb-2">{step.title}</h3>
                <p className="text-gray-400">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="section-padding" data-testid="financing-faq">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading font-bold text-responsive-section gradient-text text-center mb-12">
              FREQUENTLY ASKED QUESTIONS
            </h2>

            <div className="space-y-4">
              {[
                {
                  q: "Do I need good credit to qualify?",
                  a: "We work with multiple lenders and have options for various credit profiles. Apply to see what you qualify for!"
                },
                {
                  q: "How long does approval take?",
                  a: "Most applications receive an instant decision. In some cases, additional verification may be needed within 24-48 hours."
                },
                {
                  q: "Can I pay off my balance early?",
                  a: "Yes! There are no prepayment penalties. Pay off your balance anytime without additional fees."
                },
                {
                  q: "What can I finance?",
                  a: "You can finance any HVAC service including new installations, repairs, and maintenance plans."
                }
              ].map((faq, index) => (
                <div
                  key={index}
                  className="bg-[#101014] border-2 border-white/10 hover:border-[#0066CC] rounded-2xl p-6 transition-all hover:shadow-lg"
                  data-testid={`faq-${index}`}
                >
                  <h4 className="font-heading font-bold text-lg text-white mb-2">{faq.q}</h4>
                  <p className="text-gray-400">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
